/**
 * API-klient.
 */

const BASE = '/api';

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...init?.headers,
    },
  });
  const ct = res.headers.get('content-type') ?? '';
  const body = ct.includes('json') ? await res.json() : await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? res.statusText, body);
  }
  return body as T;
}

export type UserRole = 'admin' | 'registrar';

export interface Race {
  id: string;
  slug: string;
  name: string;
  startTime: string;
  startProcedure: string;
  /**
   * 'fixed' = klokka følger starttiden; 'committee' = komiteen starter klokka for
   * alle; 'manual' = båtene starter klokka selv.
   */
  startMode?: 'fixed' | 'committee' | 'manual';
  /** Komitéstyrt start: prosedyren er startet (startTime = faktisk start). */
  committeeStarted?: boolean;
  status: 'scheduled' | 'postponed' | 'started' | 'finished' | 'cancelled';
  postponedAt: string | null;
  postponeReason: string | null;
  description: string | null;
  venue: string | null;
  registrationDeadline: string | null;
  /** Ekstern påmeldingslenke (http/https), vises på regattalista. */
  registrationUrl?: string | null;
  /** Kun med i admin-kontekst (raceDetails). Strippes fra offentlige svar. */
  finishPassword?: string | null;
  foreningId?: string | null;
  courseId?: string | null;
}

export interface FinishRecord {
  id: string;
  position: number | null;
  sailNumber: string | null;
  elapsedMs: number | null;
  note: string | null;
  /** GPS-forslag til måltid (fra mållinje-kryssing); komiteen bekrefter. */
  autoFinishAt?: string | null;
  autoElapsedMs?: number | null;
  capturedAt: string;
  /** Slås opp fra båtregisteret via seilnummer (null om båten ikke finnes der). */
  skipperName?: string | null;
  boatName?: string | null;
}

export interface FinishList {
  race: { id: string; slug: string; name: string; status: Race['status']; startTime: string };
  records: FinishRecord[];
  editable: boolean;
}

export interface FinishWrite {
  sailNumber?: string | null;
  position?: number | null;
  elapsedMs?: number | null;
  note?: string | null;
  /** Be server tildele neste plass (brukes for «mål uten seilnr.»). */
  assignNextPosition?: boolean;
}

// GPS-sporing
export interface TrackPoint {
  lat: number;
  lon: number;
  sogKn: number | null;
  cog: number | null;
  recordedAt: string;
}

export interface TrackBoat {
  sailNumber: string;
  last: TrackPoint;
  trail: TrackPoint[];
}

export interface TrackLive {
  race: { slug: string; name: string; status: Race['status']; startTime: string };
  trailMin: number;
  serverTime: string;
  boats: TrackBoat[];
}

export interface TrackFull {
  race: { slug: string; name: string; status: Race['status']; startTime: string };
  boats: TrackBoat[];
}

export interface MyBoatTracker {
  online: boolean;
  lat: number;
  lon: number;
  sogKn: number | null;
  cog: number | null;
  satellites: number | null;
  lastSeenAt: string;
}

export interface TrackPost {
  raceSlug: string;
  sailNumber: string;
  password: string;
  lat: number;
  lon: number;
  sogKn?: number | null;
  cog?: number | null;
  accuracy?: number | null;
  recordedAt?: string;
}

export interface RaceClass {
  classId: string;
  code: string;
  name: string;
}

export interface RaceWithClasses extends Race {
  classes: RaceClass[];
}

export interface RaceEntry {
  id: string;
  boatId?: string;
  sailNumber: string;
  boatName: string;
  skipperName: string;
  crew: string | null;
  classId?: string | null;
  className?: string | null;
  ownerId?: string | null;
  ownerUsername?: string | null;
  position: number | null;
  finishNotes: string | null;
  enteredBy?: string | null;
}

export interface AdminUser {
  id: string;
  username: string;
  role: UserRole;
  fullName: string | null;
  email: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface BoatClass {
  id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
  sortOrder: number;
  flag: string;
  createdAt: string;
  updatedAt: string;
}

export interface Boat {
  id: string;
  sailNumber: string;
  boatName: string;
  skipperName: string;
  crew: string | null;
  skipperEmail?: string | null;
  skipperMobile?: string | null;
  foreningId?: string | null;
  classId: string | null;
  className?: string | null;
  ownerId: string | null;
  ownerUsername?: string | null;
  ownerFullName?: string | null;
  notes: string | null;
  active: boolean;
  password?: string | null;
  trackerId?: string | null;
  trackerImei?: string | null;
  trackerLabel?: string | null;
  createdAt: string;
}

export interface Tracker {
  id: string;
  imei: string;
  label: string | null;
  model: string;
  kind?: string;
  boatId: string | null;
  boatSail?: string | null;
  boatName?: string | null;
  active: boolean;
  lastSeenAt: string | null;
  lastLat: number | null;
  lastLon: number | null;
  lastSpeedKn: number | null;
  lastCourse: number | null;
  lastSatellites: number | null;
  lastBatteryMv: number | null;
  createdAt: string;
}

export interface TrackerPoints {
  boatSail: string | null;
  boatName: string | null;
  total: number;
  last24h: number;
  byRace: {
    raceSlug: string;
    raceName: string;
    count: number;
    first: string;
    last: string;
  }[];
  points: {
    recordedAt: string;
    lat: number;
    lon: number;
    sogKn: number | null;
    cog: number | null;
    raceName: string;
    raceSlug: string;
  }[];
}

export interface MyEntry {
  id: string;
  raceId: string;
  raceName: string;
  raceSlug: string;
  raceStartTime: string;
  raceStatus: Race['status'];
  raceRegistrationDeadline: string | null;
  boatId: string;
  sailNumber: string;
  boatName: string;
  classId: string | null;
  className: string | null;
}

/** Rad i den offentlige regattalista (/regattaer). */
export interface RegattaListItem {
  id: string;
  slug: string;
  name: string;
  startTime: string;
  status: Race['status'];
  startMode: 'fixed' | 'committee' | 'manual';
  venue: string | null;
  registrationUrl: string | null;
  registrationDeadline: string | null;
  entryCount: number;
  foreningSlug: string | null;
  foreningName: string | null;
  foreningShortName: string | null;
  foreningHasLogo: boolean | null;
}

export interface Forening {
  id: string;
  slug: string;
  name: string;
  shortName: string | null;
  active?: boolean;
  hasLogo?: boolean;
}

// --- Personlige kontoer (seilere) ---

export type KontoStatus = 'pending' | 'active' | 'rejected' | 'suspended';

export interface AppUser {
  id: string;
  email: string;
  fullName: string;
  foreningId: string;
  foreningName: string | null;
  status: KontoStatus;
  settings: Record<string, unknown> | null;
  // Roller (samlet innlogging via user_entitlements). Valgfrie — eldre klienter ignorerer dem.
  entitlements?: { foreningId: string | null; entitlement: string }[];
  isSuperadmin?: boolean;
  klubbadminForeninger?: string[];
}

export interface MyBoat {
  id: string;
  sailNumber: string;
  boatName: string;
  skipperName: string;
  crew: string | null;
  classId: string | null;
  className: string | null;
  notes: string | null;
  active: boolean;
  foreningId: string | null;
  role: string; // eier | skipper | mannskap
}

export interface MySeilas {
  entryId: string;
  raceId: string;
  raceName: string;
  raceSlug: string;
  raceStartTime: string;
  raceStatus: Race['status'];
  boatId: string;
  sailNumber: string;
  boatName: string;
  position: number | null;
  finishNotes: string | null;
}

export interface OpenRace {
  id: string;
  name: string;
  slug: string;
  startTime: string;
  status: Race['status'];
  venue: string | null;
  registrationDeadline: string | null;
  foreningId: string | null;
  foreningName: string | null;
  allowedClassIds: string[];
}

export interface KontoForesporsel {
  id: string;
  fullName: string;
  email: string;
  foreningId: string;
  foreningName: string | null;
  requestedSailNumber: string | null;
  requestedBoatName: string | null;
  requestMessage: string | null;
  createdAt: string;
}

export interface BoatClaimRow {
  id: string;
  status: string;
  role: string;
  createdAt: string;
  userId: string;
  userName: string;
  userEmail: string;
  boatId: string;
  sailNumber: string;
  boatName: string;
}

export interface KontoBruker {
  id: string;
  fullName: string;
  email: string;
  status: KontoStatus;
  foreningId: string;
  foreningName: string | null;
  createdAt: string;
  approvedAt: string | null;
  /** Arrangør (komité) for hjemklubben — kan styre komitéstyrt start. */
  arrangor?: boolean;
}

/** Startklokka ved komitéstyrt start (GET /races/:slug/klokke, pollet hvert 2. sek). */
export interface RaceClock {
  startMode: 'fixed' | 'committee' | 'manual';
  status: Race['status'];
  startTime: string;
  committeeStarted: boolean;
  postponeReason: string | null;
}

export type KomiteAction =
  | { action: 'start'; minutes: 5 | 4 | 1 }
  | { action: 'ap'; reason?: string }
  | { action: 'gjenoppta' }
  | { action: 'nullstill' };

/** Fri aktivitet (trening/tur) logget fra appen. */
export interface MinAktivitet {
  id: string;
  type: 'trening' | 'tur';
  boatId: string | null;
  sailNumber: string | null;
  boatName: string | null;
  startedAt: string;
  endedAt: string | null;
  distanceM: number | null;
  maxSogKn: number | null;
  pointCount: number;
}

export interface AktivitetSpor {
  aktivitet: MinAktivitet;
  points: { lat: number; lon: number; sogKn: number | null; recordedAt: string }[];
}

// --- Baner og merker ---

export interface AdminMark {
  id: string;
  name: string;
  source: 'fixed' | 'tracker';
  trackerId: string | null;
  lat: number | null;
  lon: number | null;
  active: boolean;
  foreningId: string;
  trackerImei?: string | null;
  trackerLabel?: string | null;
  trackerLastLat?: number | null;
  trackerLastLon?: number | null;
  trackerLastSeenAt?: string | null;
}

export interface Course {
  id: string;
  foreningId: string;
  name: string;
  description: string | null;
  active: boolean;
}

export interface CourseDetail {
  course: Course;
  start: string[];
  finish: string[];
  rounding: { markId: string; side: 'port' | 'starboard' | null }[];
}

export interface CourseGeoMark {
  markId: string;
  name: string;
  purpose: 'start' | 'finish' | 'rounding';
  seq: number;
  side: 'port' | 'starboard' | null;
  source: 'fixed' | 'tracker';
  lat: number;
  lon: number;
  online: boolean;
}

export interface CourseGeo {
  course: { id: string; name: string } | null;
  marks: CourseGeoMark[];
  startLine: { lat: number; lon: number }[] | null;
  finishLine: { lat: number; lon: number }[] | null;
}

export const api = {
  // Offentlige
  time: () => request<{ serverTime: string; epochMs: number }>('/time'),
  publicBoats: (forening?: string) =>
    request<{ sailNumber: string; boatName: string }[]>(
      `/boats${forening ? `?forening=${encodeURIComponent(forening)}` : ''}`,
    ),
  listForeninger: () => request<Forening[]>('/foreninger'),
  regattaer: (forening?: string) =>
    request<{ upcoming: RegattaListItem[]; finished: RegattaListItem[] }>(
      `/regattaer${forening ? `?forening=${encodeURIComponent(forening)}` : ''}`,
    ),
  races: (past = false, forening?: string) => {
    const p = new URLSearchParams();
    if (past) p.set('past', 'true');
    if (forening) p.set('forening', forening);
    const q = p.toString();
    return request<Race[]>(`/races${q ? `?${q}` : ''}`);
  },
  race: (slug: string) =>
    request<{
      race: Race;
      entries: RaceEntry[];
      forening: { slug: string; name: string; hasLogo: boolean } | null;
      classes: { code: string; name: string; flag: string }[];
    }>(`/races/${slug}`),
  verifyRaceAccess: (slug: string, sailNumber: string, password: string) =>
    request<{ ok: true; gated: boolean; sailNumber?: string; boatName?: string }>(
      `/races/${slug}/access`,
      { method: 'POST', body: JSON.stringify({ sailNumber, password }) },
    ),
  myBoatPosition: (slug: string, sail: string) =>
    request<{ tracker: MyBoatTracker | null }>(
      `/races/${slug}/myboat?sail=${encodeURIComponent(sail)}`,
    ),
  boatLogin: (sailNumber: string, password: string, forening?: string) =>
    request<{
      ok: true;
      sailNumber: string;
      boatName: string;
      skipperName: string;
      crew: string | null;
      skipperEmail: string | null;
      skipperMobile: string | null;
    }>('/boats/verify', { method: 'POST', body: JSON.stringify({ sailNumber, password, forening }) }),
  editBoatCard: (
    sailNumber: string,
    password: string,
    data: {
      boatName?: string;
      skipperName?: string;
      crew?: string | null;
      skipperEmail?: string | null;
      skipperMobile?: string | null;
      newPassword?: string;
      forening?: string;
    },
  ) =>
    request<{
      ok: true;
      sailNumber: string;
      boatName: string;
      skipperName: string;
      crew: string | null;
      skipperEmail: string | null;
      skipperMobile: string | null;
    }>('/boats/self', { method: 'PATCH', body: JSON.stringify({ sailNumber, password, ...data }) }),
  results: (slug: string) => request<{ race: Race; entries: RaceEntry[] }>(`/races/${slug}/results`),

  // Tidtaking / målgang (offentlig liste; skriving krever regattaens passord)
  finishList: (slug: string) => request<FinishList>(`/races/${slug}/finish`),
  finishVerify: (slug: string, loginSail: string, password: string) =>
    request<{ ok: true }>(`/races/${slug}/finish/verify`, {
      method: 'POST',
      body: JSON.stringify({ loginSail, password }),
    }),
  // Fyll tidtakingslista med påmeldte båter (hver får en rad med «Mål»-knapp).
  finishSeed: (slug: string, loginSail: string, password: string) =>
    request<{ ok: true; added: number }>(`/races/${slug}/finish/seed`, {
      method: 'POST',
      body: JSON.stringify({ loginSail, password }),
    }),
  finishCreate: (slug: string, loginSail: string, password: string, data: FinishWrite = {}) =>
    request<FinishRecord>(`/races/${slug}/finish`, {
      method: 'POST',
      body: JSON.stringify({ loginSail, password, ...data }),
    }),
  finishUpdate: (slug: string, recordId: string, loginSail: string, password: string, data: FinishWrite) =>
    request<FinishRecord>(`/races/${slug}/finish/${recordId}`, {
      method: 'PATCH',
      body: JSON.stringify({ loginSail, password, ...data }),
    }),
  // Per-rad «Mål»: server tildeler neste plass og setter medsendt tid.
  finishRecord: (slug: string, recordId: string, loginSail: string, password: string, elapsedMs: number) =>
    request<FinishRecord>(`/races/${slug}/finish/${recordId}/record`, {
      method: 'POST',
      body: JSON.stringify({ loginSail, password, elapsedMs }),
    }),

  // GPS-sporing
  postTrack: (data: TrackPost) =>
    request<{ ok: true; finished?: boolean; trackedByDevice?: boolean }>('/track', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  trackLive: (slug: string, trailMin = 5) =>
    request<TrackLive>(`/track/${slug}?trailMin=${trailMin}`),
  trackFull: (slug: string) => request<TrackFull>(`/track/${slug}/full`),
  finishDelete: (slug: string, recordId: string, loginSail: string, password: string) =>
    request<{ ok: true }>(`/races/${slug}/finish/${recordId}`, {
      method: 'DELETE',
      body: JSON.stringify({ loginSail, password }),
    }),

  // Admin-økt (åpnes via kontoen: kontoAdminBro). Direkte innlogging er stengt.
  logout: () => request<{ ok: true }>('/admin/logout', { method: 'POST' }),
  me: () =>
    request<{
      id: string;
      username: string;
      role: UserRole;
      fullName: string | null;
      email: string | null;
      // Klubben admin-økten gjelder for; null = alle klubber (superadmin).
      foreningId?: string | null;
      foreningName?: string | null;
    }>('/admin/me'),

  // Båtklasser (lese for alle innloggede; skrive kun admin)
  listClasses: (includeInactive = false) =>
    request<BoatClass[]>(`/admin/classes${includeInactive ? '?includeInactive=true' : ''}`),
  createClass: (data: {
    code: string;
    name: string;
    description?: string | null;
    sortOrder?: number;
    flag?: string;
  }) => request<BoatClass>('/admin/classes', { method: 'POST', body: JSON.stringify(data) }),
  updateClass: (id: string, data: Partial<BoatClass>) =>
    request<BoatClass>(`/admin/classes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archiveClass: (id: string) =>
    request<{ ok: true }>(`/admin/classes/${id}`, { method: 'DELETE' }),

  // Admin-båter
  listBoats: (includeInactive = false) =>
    request<Boat[]>(`/admin/boats${includeInactive ? '?includeInactive=true' : ''}`),
  createBoat: (data: Partial<Boat>) =>
    request<Boat>('/admin/boats', { method: 'POST', body: JSON.stringify(data) }),
  updateBoat: (id: string, data: Partial<Boat>) =>
    request<Boat>(`/admin/boats/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archiveBoat: (id: string) => request<{ ok: true }>(`/admin/boats/${id}`, { method: 'DELETE' }),

  // GPS-trackere (maskinvare)
  listTrackers: () => request<Tracker[]>('/admin/trackers'),
  createTracker: (data: { imei: string; label?: string | null; model?: string; boatId?: string | null }) =>
    request<Tracker>('/admin/trackers', { method: 'POST', body: JSON.stringify(data) }),
  updateTracker: (
    id: string,
    data: Partial<{ imei: string; label: string | null; model: string; boatId: string | null; active: boolean }>,
  ) => request<Tracker>(`/admin/trackers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTracker: (id: string) => request<{ ok: true }>(`/admin/trackers/${id}`, { method: 'DELETE' }),
  trackerPoints: (id: string, limit = 100, race?: string) =>
    request<TrackerPoints>(
      `/admin/trackers/${id}/points?limit=${limit}${race ? `&race=${encodeURIComponent(race)}` : ''}`,
    ),
  /** Alle sporinger for en båt (telefon + tracker). Samme format som trackerPoints. */
  boatPoints: (id: string, limit = 100, race?: string) =>
    request<TrackerPoints>(
      `/admin/boats/${id}/points?limit=${limit}${race ? `&race=${encodeURIComponent(race)}` : ''}`,
    ),

  // Foreninger (seilforeninger)
  listForeningerAdmin: () => request<Forening[]>('/admin/foreninger'),
  createForening: (data: { name: string; shortName?: string | null }) =>
    request<Forening>('/admin/foreninger', { method: 'POST', body: JSON.stringify(data) }),
  updateForening: (
    id: string,
    data: Partial<{ name: string; shortName: string | null; active: boolean; logo: string | null }>,
  ) => request<Forening>(`/admin/foreninger/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteForening: (id: string) =>
    request<{ ok: true }>(`/admin/foreninger/${id}`, { method: 'DELETE' }),

  // Admin-regattaer
  listRaces: () => request<Race[]>('/admin/races'),
  createRace: (data: {
    name: string;
    startTime: string;
    description?: string;
    venue?: string;
    registrationDeadline?: string | null;
    registrationUrl?: string | null;
    startMode?: 'fixed' | 'committee' | 'manual';
    allowedClassIds?: string[];
    foreningId?: string | null;
    courseId?: string | null;
  }) => request<Race>('/admin/races', { method: 'POST', body: JSON.stringify(data) }),
  updateRace: (
    id: string,
    data: Partial<Race> & { allowedClassIds?: string[]; registrationDeadline?: string | null },
  ) => request<Race>(`/admin/races/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancelRace: (id: string) => request<{ ok: true }>(`/admin/races/${id}`, { method: 'DELETE' }),
  deleteRace: (id: string) =>
    request<{ ok: true; entriesRemoved: number }>(`/admin/races/${id}/purge`, {
      method: 'DELETE',
    }),
  raceDetails: (id: string) =>
    request<{
      race: Race;
      entries: (RaceEntry & { boatId: string })[];
      classes: RaceClass[];
    }>(`/admin/races/${id}`),

  postponeRace: (id: string, data: { newStartTime?: string; reason?: string }) =>
    request<Race>(`/admin/races/${id}/postpone`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  resumeRace: (id: string, newStartTime: string) =>
    request<Race>(`/admin/races/${id}/resume`, {
      method: 'POST',
      body: JSON.stringify({ newStartTime }),
    }),
  finalizeRace: (id: string) => request<Race>(`/admin/races/${id}/finalize`, { method: 'POST' }),

  // Admin-påmeldinger
  addEntry: (raceId: string, boatId: string) =>
    request<RaceEntry>(`/admin/races/${raceId}/entries`, {
      method: 'POST',
      body: JSON.stringify({ boatId }),
    }),
  removeEntry: (entryId: string) =>
    request<{ ok: true }>(`/admin/races/entries/${entryId}`, { method: 'DELETE' }),
  updateEntry: (entryId: string, data: { position: number | null; finishNotes?: string | null }) =>
    request<RaceEntry>(`/admin/races/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Brukere (admin og registrar)
  listAdmins: () => request<AdminUser[]>('/admin/admins'),
  createAdmin: (data: {
    username: string;
    password: string;
    role?: UserRole;
    fullName?: string | null;
    email?: string | null;
  }) => request<AdminUser>('/admin/admins', { method: 'POST', body: JSON.stringify(data) }),
  updateAdmin: (
    id: string,
    data: {
      role?: UserRole;
      fullName?: string | null;
      email?: string | null;
      password?: string;
    },
  ) =>
    request<AdminUser>(`/admin/admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteAdmin: (id: string) =>
    request<{ ok: true }>(`/admin/admins/${id}`, { method: 'DELETE' }),

  // Registrar — selvbetjening
  myBoats: () => request<Boat[]>('/registrar/boats'),
  createMyBoat: (data: {
    sailNumber: string;
    boatName: string;
    skipperName: string;
    crew?: string | null;
    classId?: string | null;
    notes?: string | null;
  }) => request<Boat>('/registrar/boats', { method: 'POST', body: JSON.stringify(data) }),
  updateMyBoat: (id: string, data: Partial<Boat>) =>
    request<Boat>(`/registrar/boats/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  archiveMyBoat: (id: string) =>
    request<{ ok: true }>(`/registrar/boats/${id}`, { method: 'DELETE' }),

  myAvailableRaces: () => request<RaceWithClasses[]>('/registrar/races'),
  myEntries: () => request<MyEntry[]>('/registrar/entries'),
  enrollMyBoat: (raceId: string, boatId: string) =>
    request<{ id: string }>(`/registrar/races/${raceId}/entries`, {
      method: 'POST',
      body: JSON.stringify({ boatId }),
    }),
  withdrawMyEntry: (entryId: string) =>
    request<{ ok: true }>(`/registrar/entries/${entryId}`, { method: 'DELETE' }),

  // Personlige kontoer (seilere)
  kontoForesporsel: (data: {
    fullName: string;
    email: string;
    foreningId: string;
    sailNumber?: string | null;
    boatName?: string | null;
    message?: string | null;
  }) =>
    request<{ status: 'pending' | 'exists' }>('/konto/foresporsel', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  kontoLogin: (email: string, password: string) =>
    request<{ user: { id: string; email: string; fullName: string; foreningId: string } }>(
      '/konto/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
  kontoLogout: () => request<{ ok: true }>('/konto/logout', { method: 'POST' }),
  kontoMe: () => request<AppUser>('/konto/me'),
  // Bro til admin-delen (setter rc_session). Superadmin → global; klubbadmin → sin klubb.
  // Uten foreningId lar serveren superadmin bli global og klubbadmin m/ én klubb velges automatisk.
  kontoAdminBro: (foreningId?: string) =>
    request<{ ok: true; foreningId: string | null }>('/konto/admin-bro', {
      method: 'POST',
      ...(foreningId ? { body: JSON.stringify({ foreningId }) } : {}),
    }),
  kontoSettPassord: (token: string, password: string) =>
    request<{ ok: true }>('/konto/sett-passord', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  kontoGlemtPassord: (email: string) =>
    request<{ ok: true }>('/konto/glemt-passord', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  // Min side (innlogget bruker)
  brukerBaater: () => request<MyBoat[]>('/bruker/baater'),
  brukerCreateBaat: (data: {
    sailNumber: string;
    boatName: string;
    skipperName?: string;
    crew?: string | null;
    classId?: string | null;
    notes?: string | null;
  }) =>
    request<{ status: 'created'; boat: Boat } | { status: 'claim'; boatId: string }>(
      '/bruker/baater',
      { method: 'POST', body: JSON.stringify(data) },
    ),
  brukerUpdateBaat: (
    id: string,
    data: {
      boatName?: string;
      skipperName?: string;
      crew?: string | null;
      classId?: string | null;
      notes?: string | null;
    },
  ) => request<Boat>(`/bruker/baater/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  brukerRemoveBaat: (id: string) =>
    request<{ ok: true }>(`/bruker/baater/${id}`, { method: 'DELETE' }),
  brukerSeilaser: () => request<MySeilas[]>('/bruker/seilaser'),
  // Kryss-klubb-påmelding: åpne regattaer (alle klubber), meld på / trekk egen båt.
  brukerApneRegattaer: () => request<OpenRace[]>('/bruker/apne-regattaer'),
  brukerMeldPaa: (raceId: string, boatId: string) =>
    request<{ id: string }>('/bruker/seilaser', {
      method: 'POST',
      body: JSON.stringify({ raceId, boatId }),
    }),
  brukerTrekkPaamelding: (entryId: string) =>
    request<{ ok: true }>(`/bruker/seilaser/${entryId}`, { method: 'DELETE' }),
  brukerInnstillinger: () => request<Record<string, unknown>>('/bruker/innstillinger'),
  brukerSetInnstillinger: (settings: Record<string, unknown>) =>
    request<Record<string, unknown>>('/bruker/innstillinger', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  brukerSetProfil: (data: { fullName: string; foreningId: string }) =>
    request<{ ok: true }>('/bruker/profil', { method: 'PUT', body: JSON.stringify(data) }),
  brukerByttPassord: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/bruker/passord', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  brukerAktiviteter: () => request<MinAktivitet[]>('/bruker/aktiviteter'),
  brukerAktivitet: (id: string) => request<AktivitetSpor>(`/bruker/aktiviteter/${id}`),
  brukerSlettAktivitet: (id: string) =>
    request<{ ok: true }>(`/bruker/aktiviteter/${id}`, { method: 'DELETE' }),

  // Admin — kontoforvaltning
  adminKontoForesporsler: () => request<KontoForesporsel[]>('/admin/kontoer/foresporsler'),
  adminGodkjennKonto: (id: string, boatId?: string | null) =>
    request<{ ok: true; boatId: string | null }>(`/admin/kontoer/foresporsler/${id}/godkjenn`, {
      method: 'POST',
      body: JSON.stringify({ boatId: boatId ?? null }),
    }),
  adminAvslaaKonto: (id: string, reason?: string) =>
    request<{ ok: true }>(`/admin/kontoer/foresporsler/${id}/avslaa`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  adminTilknytninger: () => request<BoatClaimRow[]>('/admin/kontoer/tilknytninger'),
  adminGodkjennTilknytning: (id: string) =>
    request<{ ok: true }>(`/admin/kontoer/tilknytninger/${id}/godkjenn`, { method: 'POST' }),
  adminAvslaaTilknytning: (id: string) =>
    request<{ ok: true }>(`/admin/kontoer/tilknytninger/${id}/avslaa`, { method: 'POST' }),
  adminInviter: (email: string, foreningId: string) =>
    request<{ ok: true }>('/admin/kontoer/inviter', {
      method: 'POST',
      body: JSON.stringify({ email, foreningId }),
    }),
  adminBrukere: (status?: string) =>
    request<KontoBruker[]>(
      `/admin/kontoer/brukere${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  adminSuspenderBruker: (id: string) =>
    request<{ ok: true }>(`/admin/kontoer/brukere/${id}/suspender`, { method: 'POST' }),
  adminAktiverBruker: (id: string) =>
    request<{ ok: true }>(`/admin/kontoer/brukere/${id}/aktiver`, { method: 'POST' }),
  adminSettArrangor: (id: string, on: boolean) =>
    request<{ ok: true; arrangor: boolean }>(`/admin/kontoer/brukere/${id}/arrangor`, {
      method: 'POST',
      body: JSON.stringify({ on }),
    }),

  // Komitéstyrt start
  raceClock: (slug: string) => request<RaceClock>(`/races/${slug}/klokke`),
  raceKomite: (slug: string) => request<{ allowed: boolean }>(`/races/${slug}/komite`),
  raceKomiteAction: (slug: string, body: KomiteAction) =>
    request<RaceClock>(`/races/${slug}/komite`, { method: 'POST', body: JSON.stringify(body) }),

  // Bane-geometri (offentlig)
  raceCourse: (slug: string) => request<CourseGeo>(`/races/${slug}/course`),
  courseGeo: (id: string) => request<CourseGeo>(`/courses/${id}/geo`),

  // Admin — merker
  listMarks: () => request<AdminMark[]>('/admin/marks'),
  createMark: (data: {
    name: string;
    source: 'fixed' | 'tracker';
    trackerId?: string | null;
    lat?: number | null;
    lon?: number | null;
    active?: boolean;
  }) => request<AdminMark>('/admin/marks', { method: 'POST', body: JSON.stringify(data) }),
  updateMark: (
    id: string,
    data: Partial<{
      name: string;
      source: 'fixed' | 'tracker';
      trackerId: string | null;
      lat: number | null;
      lon: number | null;
      active: boolean;
    }>,
  ) => request<AdminMark>(`/admin/marks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMark: (id: string) => request<{ ok: true }>(`/admin/marks/${id}`, { method: 'DELETE' }),

  // Admin — baner
  listCourses: () => request<Course[]>('/admin/courses'),
  getCourse: (id: string) => request<CourseDetail>(`/admin/courses/${id}`),
  createCourse: (data: { name: string; description?: string | null }) =>
    request<Course>('/admin/courses', { method: 'POST', body: JSON.stringify(data) }),
  updateCourse: (id: string, data: Partial<{ name: string; description: string | null; active: boolean }>) =>
    request<Course>(`/admin/courses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  setCourseMarks: (
    id: string,
    data: {
      start?: string[];
      finish?: string[];
      rounding?: { markId: string; side?: 'port' | 'starboard' | null }[];
    },
  ) => request<{ ok: true; count: number }>(`/admin/courses/${id}/marks`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCourse: (id: string) => request<{ ok: true }>(`/admin/courses/${id}`, { method: 'DELETE' }),
};
